import React, { useEffect, useState } from 'react';
import { format, differenceInCalendarDays, subDays, startOfWeek, startOfMonth } from 'date-fns';
import { CalendarDays, Clock, ArrowRight, ArrowLeft, ChevronRight } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent as PopoverContentBase } from './ui/popover';
import { Calendar as CalendarBase } from './ui/calendar';

const PopoverContent: any = PopoverContentBase;
const Calendar: any = CalendarBase;

interface DateRangePickerProps {
  value: { from: Date; to: Date };
  onChange: (range: { from: Date; to: Date }) => void;
  minDate: Date;
  maxDate: Date;
  maxSpanDays?: number;
  testIdPrefix: string;
  /** Shopify-native left preset sidebar (Today/Yesterday/Last/Period to date/Custom range) —
   * opt-in so every other module keeps the plain two-month calendar it already has. */
  presets?: boolean;
}

const toLong = (d?: Date) => (d ? format(d, 'MMMM d, yyyy') : 'Select date');

const startOfQuarter = (d: Date) => { const m = Math.floor(d.getMonth() / 3) * 3; return new Date(d.getFullYear(), m, 1); };

type MenuView = 'root' | 'last' | 'period';

const LAST_PRESETS = [
  { key: 'last7', label: 'Last 7 days', range: (today: Date) => ({ from: subDays(today, 6), to: today }) },
  { key: 'last30', label: 'Last 30 days', range: (today: Date) => ({ from: subDays(today, 29), to: today }) },
  { key: 'last90', label: 'Last 90 days', range: (today: Date) => ({ from: subDays(today, 89), to: today }) },
];
const PERIOD_PRESETS = [
  { key: 'wtd', label: 'Week to date', range: (today: Date) => ({ from: startOfWeek(today, { weekStartsOn: 0 }), to: today }) },
  { key: 'mtd', label: 'Month to date', range: (today: Date) => ({ from: startOfMonth(today), to: today }) },
  { key: 'qtd', label: 'Quarter to date', range: (today: Date) => ({ from: startOfQuarter(today), to: today }) },
];

/**
 * Shopify-style date-range picker shared across every module — two months side-by-side, a black
 * / grey / white palette, and an explicit Apply / Cancel footer. All clicks while the popover is
 * open only touch local draft state; `onChange` (and therefore every page's downstream API
 * refetch) fires exactly once, on "Apply". Closing any other way — Cancel, outside click, Escape
 * — discards the draft and reverts to the last applied range, so the dashboard never refetches
 * mid-selection.
 */
export function DateRangePicker({ value, onChange, minDate, maxDate, maxSpanDays = 90, testIdPrefix, presets = false }: DateRangePickerProps) {
  const [open, setOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState<Date | undefined>(value.from);
  const [draftTo, setDraftTo] = useState<Date | undefined>(value.to);
  const [pickingStart, setPickingStart] = useState(true);
  const [menuView, setMenuView] = useState<MenuView>('root');
  const [activePreset, setActivePreset] = useState<string | null>(null);
  const [calendarKey, setCalendarKey] = useState(0);

  useEffect(() => {
    setDraftFrom(value.from);
    setDraftTo(value.to);
    setPickingStart(true);
    setMenuView('root');
    setActivePreset(null);
    setCalendarKey((k) => k + 1);
  }, [value]);

  const clampDate = (d: Date) => (d < minDate ? minDate : d > maxDate ? maxDate : d);

  const handleDayClick = (day: Date, modifiers: any) => {
    if (modifiers?.disabled) return;
    setActivePreset('custom');
    if (pickingStart || !draftFrom) {
      setDraftFrom(day);
      setDraftTo(undefined);
      setPickingStart(false);
    } else {
      const from = draftFrom;
      const newFrom = day < from ? day : from;
      const newTo = day < from ? from : day;
      setDraftFrom(newFrom);
      setDraftTo(newTo);
      setPickingStart(true);
    }
  };

  const applyPresetRange = (key: string, range: { from: Date; to: Date }) => {
    setActivePreset(key);
    setDraftFrom(clampDate(range.from));
    setDraftTo(clampDate(range.to));
    setPickingStart(true);
    setCalendarKey((k) => k + 1);
  };

  const resetDraft = () => {
    setDraftFrom(value.from);
    setDraftTo(value.to);
    setPickingStart(true);
    setMenuView('root');
    setActivePreset(null);
  };

  const handleApply = () => {
    if (!draftFrom) { setOpen(false); return; }
    let from = draftFrom;
    let to = draftTo || draftFrom;
    if (differenceInCalendarDays(to, from) > maxSpanDays) to = new Date(from.getTime() + maxSpanDays * 86400000);
    onChange({ from: clampDate(from), to: clampDate(to) });
    setOpen(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) resetDraft();
    setOpen(next);
  };

  const rangeLabel = `${format(value.from, 'dd MMM')} — ${format(value.to, 'dd MMM yyyy')}`;
  const today = new Date();
  const yesterday = subDays(today, 1);

  const itemCls = (key: string) => `w-full text-left px-3 py-2 rounded-lg text-[13px] transition-colors flex items-center justify-between ${
    activePreset === key ? 'bg-slate-100 font-semibold text-slate-900' : 'text-slate-700 hover:bg-slate-50'
  }`;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button data-testid={`${testIdPrefix}-date-range-trigger`} className="px-4 py-2.5 rounded-xl flex items-center gap-2.5 text-xs font-semibold border bg-white text-slate-700 border-slate-200 hover:border-slate-400 transition-colors">
          <CalendarDays className="h-4 w-4 text-slate-500" />
          <span>{rangeLabel}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-white border-slate-200 shadow-xl" align="end">
        <div className="flex items-start">
          {presets && (
            <div data-testid={`${testIdPrefix}-preset-sidebar`} className="w-44 shrink-0 border-r border-slate-100 p-3 flex flex-col gap-0.5">
              {menuView === 'root' && (
                <>
                  <button data-testid={`${testIdPrefix}-preset-today`} className={itemCls('today')} onClick={() => applyPresetRange('today', { from: today, to: today })}>Today</button>
                  <button data-testid={`${testIdPrefix}-preset-yesterday`} className={itemCls('yesterday')} onClick={() => applyPresetRange('yesterday', { from: yesterday, to: yesterday })}>Yesterday</button>
                  <button data-testid={`${testIdPrefix}-preset-last`} className={itemCls('__last-group')} onClick={() => setMenuView('last')}>
                    Last <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
                  </button>
                  <button data-testid={`${testIdPrefix}-preset-period-to-date`} className={itemCls('__period-group')} onClick={() => setMenuView('period')}>
                    Period to date <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
                  </button>
                  <div className="h-px bg-slate-100 my-1.5" />
                  <button data-testid={`${testIdPrefix}-preset-custom-range`} className={itemCls('custom')} onClick={() => setActivePreset('custom')}>Custom range</button>
                </>
              )}
              {menuView === 'last' && (
                <>
                  <button data-testid={`${testIdPrefix}-preset-back`} onClick={() => setMenuView('root')} className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-800 mb-2 px-1">
                    <ArrowLeft className="h-3.5 w-3.5" /> Last
                  </button>
                  {LAST_PRESETS.map((p) => (
                    <button key={p.key} data-testid={`${testIdPrefix}-preset-${p.key}`} className={itemCls(p.key)} onClick={() => applyPresetRange(p.key, p.range(today))}>{p.label}</button>
                  ))}
                </>
              )}
              {menuView === 'period' && (
                <>
                  <button data-testid={`${testIdPrefix}-preset-back`} onClick={() => setMenuView('root')} className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-800 mb-2 px-1">
                    <ArrowLeft className="h-3.5 w-3.5" /> Period to date
                  </button>
                  {PERIOD_PRESETS.map((p) => (
                    <button key={p.key} data-testid={`${testIdPrefix}-preset-${p.key}`} className={itemCls(p.key)} onClick={() => applyPresetRange(p.key, p.range(today))}>{p.label}</button>
                  ))}
                </>
              )}
            </div>
          )}

          <div className="p-4">
            <div className="flex items-center gap-3 mb-4">
              <div data-testid={`${testIdPrefix}-from-input`} className="px-3.5 py-2.5 rounded-lg border border-slate-300 text-sm text-slate-800 font-medium min-w-[150px]">
                {toLong(draftFrom)}
              </div>
              <ArrowRight className="h-3.5 w-3.5 text-slate-400 shrink-0" />
              <div data-testid={`${testIdPrefix}-to-input`} className="px-3.5 py-2.5 rounded-lg border border-slate-300 text-sm text-slate-800 font-medium min-w-[150px]">
                {toLong(draftTo || draftFrom)}
              </div>
              <div className="p-2.5 rounded-lg border border-slate-300 text-slate-400 shrink-0">
                <Clock className="h-4 w-4" />
              </div>
            </div>

            <Calendar
              key={calendarKey}
              mode="range"
              showOutsideDays={false}
              selected={{ from: draftFrom, to: draftTo } as any}
              onDayClick={handleDayClick}
              numberOfMonths={2}
              defaultMonth={draftFrom}
              data-testid={`${testIdPrefix}-calendar`}
              fromDate={minDate}
              toDate={maxDate}
              disabled={[{ before: minDate }, { after: maxDate }]}
              classNames={{
                caption_label: 'text-base font-semibold text-slate-900',
                nav_button: 'h-7 w-7 bg-transparent p-0 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-md',
                head_cell: 'text-slate-400 rounded-md w-9 font-medium text-[0.75rem]',
                cell: 'relative w-9 p-0 text-center',
                day: 'h-9 w-9 p-0 font-normal text-slate-700 hover:bg-slate-100 rounded-md aria-selected:opacity-100',
                day_today: 'font-bold text-slate-900',
                day_range_start: '!bg-slate-900 !text-white hover:!bg-slate-900 hover:!text-white focus:!bg-slate-900 focus:!text-white rounded-md',
                day_range_end: '!bg-slate-900 !text-white hover:!bg-slate-900 hover:!text-white focus:!bg-slate-900 focus:!text-white rounded-md',
                day_range_middle: 'aria-selected:!bg-slate-100 aria-selected:!text-slate-800 rounded-none',
                day_disabled: 'text-slate-300',
              }}
            />

            <div className="flex items-center justify-end gap-2 pt-4 mt-3 border-t border-slate-100">
              <button data-testid={`${testIdPrefix}-cancel-button`} onClick={() => { resetDraft(); setOpen(false); }} className="px-4 py-2 rounded-lg text-xs font-semibold text-slate-600 border border-slate-200 hover:bg-slate-50 transition-colors">
                Cancel
              </button>
              <button data-testid={`${testIdPrefix}-apply-button`} onClick={handleApply} className="px-5 py-2 rounded-lg text-xs font-semibold text-white bg-slate-900 hover:bg-black transition-colors">
                Apply
              </button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
